import React, { useState, useEffect } from 'react';
import Button from '../Button/Button';
import UploadDocumentModal from '../UploadDocumentModal/UploadDocumentModal';
import SectionPliable from '../SectionPliable/SectionPliable';
import './MesDocuments.css';
import { useAuth } from '../../contexts/AuthContext';
import { useDocumentPermissions } from '../../hooks/useDocumentPermissions';

interface Document {
  id: number;
  id_contrat: number;
  type: string;
  commentaire: string;
  created_at: string;
  updated_at: string;
}

interface ContratDocument {
  type: string;
  nom: string;
  binaire: string;
}

interface Contrat {
  id: number;
  numero: string;
  created_at: string;
  updated_at: string;
  documents: ContratDocument[];
}

interface DocumentsResponse {
  status: string;
  data: {
    entite_id: number;
    documents: Document[];
    documents_par_type: Record<string, Document[]>;
    total_documents: number;
  };
}

interface ContratsResponse {
  status: string;
  data: {
    entite_id: number;
    contrats: Contrat[];
    total_contrats: number;
  };
}

const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB

const MesDocuments = () => {
  const { user } = useAuth();
  const userRole = user?.role || '';
  
  // Hook pour les permissions de documents (CORE-12)
  const { checkPermission, isCentralisGenerated, isCentralisUser } = useDocumentPermissions();
  
  // Vérifier si l'utilisateur est un prestataire (tous les types)
  const isPrestataire = ['Prestataire', 'Prestataire signataire', 'Prestataire signataire admin'].includes(userRole);
  
  // Vérifier si l'utilisateur peut modifier les documents (seulement admin ou Centralis)
  const canModifyDocuments = userRole === 'Prestataire signataire admin' || isCentralisUser;

  // Fonction pour verifier si un type de document peut etre modifie (CORE-12)
  const canUpdateDocType = (type: string, hasDocument: boolean): boolean => {
    const permission = checkPermission(type, hasDocument);
    return hasDocument ? permission.canModify : permission.canUpload;
  };

  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentsByType, setDocumentsByType] = useState<Record<string, Document[]>>({});
  const [contrats, setContrats] = useState<Contrat[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [selectedDocType, setSelectedDocType] = useState<string>('');
  const [idContrat, setIdContrat] = useState<number | null>(null);

  // Fonction pour normaliser les noms de types de documents
  const normalizeDocumentType = (type: string): string => {
    const typeMapping: Record<string, string> = {
      'Attestation fiscale': 'Attestations fiscales et sociales',
      'Attestation sociale': 'Attestations fiscales et sociales',
      "Attestation d'assurance": "Attestations d'assurance",
      "Attestation assurance": "Attestations d'assurance",
      "Attestations d'assurance": "Attestations d'assurance",
      'Responsabilité Civile Professionnelle': "Attestations d'assurance"
    };
    
    return typeMapping[type] || type;
  };

  // Récupérer les documents depuis l'API
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Token non trouvé');
        }

        const apiUrl = process.env.REACT_APP_API_URL || '';
        console.log('Fetching documents from:', `${apiUrl}/documents/my-entite`);
        console.log('User role:', userRole);
        console.log('Is prestataire:', isPrestataire);
        
        const response = await fetch(`${apiUrl}/documents/my-entite`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        console.log('Response status:', response.status);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Error response:', errorData);
          throw new Error(errorData.message || 'Erreur lors de la récupération des documents');
        }

        const data: DocumentsResponse = await response.json();
        console.log('Documents data:', data);
        
        if (data.status === 'success') {
          // Normaliser les types de documents
          const normalizedDocuments = data.data.documents.map(doc => ({
            ...doc,
            type: normalizeDocumentType(doc.type)
          }));
          
          // Regrouper les documents par type normalisé
          const groupedByType: Record<string, Document[]> = {};
          normalizedDocuments.forEach(doc => {
            if (!groupedByType[doc.type]) {
              groupedByType[doc.type] = [];
            }
            groupedByType[doc.type].push(doc);
          });
          
          setDocuments(normalizedDocuments);
          setDocumentsByType(groupedByType);
          
          // Récupérer l'id_contrat du premier document (tous les documents d'une entité ont le même contrat)
          if (data.data.documents.length > 0) {
            setIdContrat(data.data.documents[0].id_contrat);
          }
        }
      } catch (err) {
        console.error('Erreur:', err);
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };

    if (isPrestataire) {
      console.log('Fetching documents for prestataire');
      fetchDocuments();
      fetchContrats();
    } else {
      console.log('Not a prestataire, skipping document fetch');
      setLoading(false);
    }
  }, [isPrestataire, userRole]);

  // Récupérer les contrats (CCAP + AE) depuis l'API
  const fetchContrats = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token non trouvé');
      }

      const apiUrl = process.env.REACT_APP_API_URL || '';
      console.log('Fetching contrats from:', `${apiUrl}/documents/my-entite-contrats`);
      
      const response = await fetch(`${apiUrl}/documents/my-entite-contrats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('Contrats response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        throw new Error(errorData.message || 'Erreur lors de la récupération des contrats');
      }

      const data: ContratsResponse = await response.json();
      console.log('Contrats data:', data);

      if (data.status === 'success' && data.data && data.data.contrats) {
        setContrats(data.data.contrats);
      }
    } catch (err) {
      console.error('Erreur contrats:', err);
      // Ne pas bloquer l'affichage si les contrats ne se chargent pas
    }
  };

  // Fonction pour télécharger un document
  const handleDownload = async (doc: Document) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Token non trouvé');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/documents/${doc.id}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement du document');
      }

      // Créer un blob à partir de la réponse
      const blob = await response.blob();
      
      // Créer un lien temporaire et déclencher le téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${doc.type}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur lors du téléchargement:', err);
      alert('Erreur lors du téléchargement du document');
    }
  };

  // Fonction pour télécharger un PDF de contrat (CCAP ou AE)
  const handleContratDownload = (contratDoc: ContratDocument, numeroContrat: string) => {
    try {
      // Décoder le base64 et créer un blob
      const byteCharacters = atob(contratDoc.binaire);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Créer un lien temporaire et déclencher le téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${contratDoc.type}_${numeroContrat}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur lors du téléchargement du contrat:', err);
      alert('Erreur lors du téléchargement du document de contrat');
    }
  };

  // Logo upload handler (conservé pour compatibilité)
  const handleLogoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.jpeg,.jpg,.png';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
          alert('Format accepté : jpeg ou png');
          return;
        }
        if (file.size > MAX_LOGO_SIZE) {
          alert('Logo trop lourd (max 2Mo)');
          return;
        }
        // TODO: Implémenter l'upload du logo vers l'API
        alert('Upload de logo à implémenter');
      }
    };
    input.click();
  };

  // Generic upload handler for sections that allow adding documents
  const handleFileUpload = (section: string) => {
    setSelectedDocType(section);
    setUploadModalOpen(true);
  };

  // Fonction pour uploader un document
  const handleUploadDocument = async (file: File, type: string, commentaire?: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token non trouvé');
      }

      // Récupérer l'id_contrat à utiliser
      let contratId = idContrat;

      // Si on n'a pas d'id_contrat, essayer de le récupérer depuis l'entité
      if (!contratId) {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        
        // Récupérer les informations de l'entité du prestataire
        const userResponse = await fetch(`${apiUrl}/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!userResponse.ok) {
          throw new Error('Impossible de récupérer les informations utilisateur');
        }

        const userData = await userResponse.json();
        
        // Vérifier si l'utilisateur est un interlocuteur ou gestionnaire
        let idEntite = null;
        const roles = userData.data?.roles || [];
        
        // Chercher l'id_entite selon le rôle
        if (roles.some((r: any) => r.name === 'interlocuteur' || r.name === 'gestionnaire' || r.name === 'signataire')) {
          // Requête pour récupérer l'entité de l'utilisateur
          const roleEndpoint = roles.some((r: any) => r.name === 'interlocuteur') 
            ? '/interlocuteurs/me'
            : roles.some((r: any) => r.name === 'gestionnaire')
            ? '/gestionnaires/me'
            : '/signataires/me';

          const roleResponse = await fetch(`${apiUrl}${roleEndpoint}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (roleResponse.ok) {
            const roleData = await roleResponse.json();
            idEntite = roleData.data?.id_entite;
          }
        }

        if (!idEntite) {
          throw new Error('Impossible de trouver l\'entité associée à votre compte.');
        }

        // Récupérer le contrat de l'entité
        const contratsResponse = await fetch(`${apiUrl}/contrats?id_entite=${idEntite}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!contratsResponse.ok) {
          throw new Error('Impossible de récupérer les contrats de votre entité.');
        }

        const contratsData = await contratsResponse.json();
        if (contratsData.data && contratsData.data.length > 0) {
          contratId = contratsData.data[0].id;
        } else {
          throw new Error('Aucun contrat trouvé pour votre entité. Veuillez contacter l\'administrateur.');
        }
      }

      // Vérifier qu'on a bien un contratId
      if (!contratId) {
        throw new Error('Impossible de déterminer le contrat pour uploader le document.');
      }

      const formData = new FormData();
      formData.append('id_contrat', contratId.toString());
      formData.append('type', type);
      formData.append('document_file', file);
      if (commentaire) {
        formData.append('commentaire', commentaire);
      }

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de l\'upload du document');
      }

      // Recharger les documents après l'upload
      window.location.reload(); // Solution simple, vous pouvez améliorer en rafraîchissant uniquement les données
    } catch (err) {
      console.error('Erreur lors de l\'upload:', err);
      throw err;
    }
  };

  // Replace handler for attestation (only one file)
  const handleAttestationReplace = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // TODO: Implémenter le remplacement vers l'API
        alert('Remplacement d\'attestation à implémenter');
      }
    };
    input.click();
  };

  // Si l'utilisateur est un acheteur ou un validateur, ne pas afficher le composant
  if (userRole === 'acheteur' || userRole === 'validateur') {
    return null;
  }

  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="mes-documents">
        <p className="loading-text">Chargement des documents...</p>
      </div>
    );
  }

  // Affichage en cas d'erreur
  if (error) {
    return (
      <div className="mes-documents">
        <p className="error-text">Erreur : {error}</p>
      </div>
    );
  }

  return (
    <div className="mes-documents">
      {isPrestataire && (
        <>
          {/* Section Documents par type */}
          {Object.keys(documentsByType).length > 0 ? (
            <>
              {Object.entries(documentsByType).map(([type, docs]) => (
                <SectionPliable key={type} title={`${type} (${docs.length})`}>
                    <div className="documents-grid">
                      {docs.map((doc) => (
                        <div 
                          key={doc.id} 
                          className="document-card clickable"
                          onClick={() => handleDownload(doc)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="document-content">
                            <div className="document-details">
                              <div className="document-name" style={{ textAlign: 'center' }}>{doc.type}</div>
                              <div className="document-date">
                                Dernière mise à jour le : {new Date(doc.updated_at).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="add-document-container">
                      {canModifyDocuments && canUpdateDocType(type, docs.length > 0) ? (
                        <Button
                          onClick={() => handleFileUpload(type)}
                          value="Actualiser le document"
                          variant="secondary"
                          size="small"
                        />
                      ) : !canModifyDocuments ? (
                        <div className="non-updatable-notice" style={{ 
                          fontSize: '0.85rem', 
                          color: '#666', 
                          fontStyle: 'italic',
                          padding: '8px 12px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px'
                        }}>
                          Seul l'administrateur peut modifier les documents
                        </div>
                      ) : (
                        <div className="non-updatable-notice" style={{ 
                          fontSize: '0.85rem', 
                          color: '#666', 
                          fontStyle: 'italic',
                          padding: '8px 12px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px'
                        }}>
                          Pour des raisons legales, veuillez faire une demande au support pour modification.
                        </div>
                      )}
                    </div>
                </SectionPliable>
              ))}
            </>
          ) : (
            <div className="info-text">Aucun document disponible</div>
          )}
          
          {/* Sections pour KBIS et Déclaration sur l'honneur - toujours visibles */}
          <SectionPliable title={<>KBIS <span style={{ fontSize: '0.75rem', color: '#f97316', fontWeight: 'normal' }}>(requis)</span></>}>
              {documentsByType['KBIS'] && 
               documentsByType['KBIS'].length > 0 ? (
                <>
                  <div className="documents-grid">
                    {documentsByType['KBIS'].map((doc) => (
                      <div 
                        key={doc.id} 
                        className="document-card clickable"
                        onClick={() => handleDownload(doc)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="document-content">
                          <div className="document-details">
                            <div className="document-name" style={{ textAlign: 'center' }}>{doc.type}</div>
                            <div className="document-date">
                              Dernière mise à jour le : {new Date(doc.updated_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="add-document-container">
                    {canModifyDocuments && canUpdateDocType('kbis', true) ? (
                      <Button
                        onClick={() => handleFileUpload('KBIS')}
                        value="Actualiser le document"
                        variant="secondary"
                        size="small"
                      />
                    ) : !canModifyDocuments ? (
                      <div className="non-updatable-notice" style={{ 
                        fontSize: '0.85rem', 
                        color: '#666', 
                        fontStyle: 'italic',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                      }}>
                        Seul l'administrateur peut modifier les documents
                      </div>
                    ) : (
                      <div className="non-updatable-notice" style={{ 
                        fontSize: '0.85rem', 
                        color: '#666', 
                        fontStyle: 'italic',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                      }}>
                        Pour des raisons legales, veuillez faire une demande au support pour modification.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="add-document-container">
                  {canModifyDocuments && canUpdateDocType('kbis', false) ? (
                    <Button
                      onClick={() => handleFileUpload('KBIS')}
                      value="Ajouter le document"
                      variant="primary"
                      size="small"
                    />
                  ) : (
                    <div className="info-text">Aucun document disponible</div>
                  )}
                </div>
              )}
          </SectionPliable>

          <SectionPliable title={<>Déclaration sur l'honneur <span style={{ fontSize: '0.75rem', color: '#f97316', fontWeight: 'normal' }}>(requis)</span></>}>
              {documentsByType["Déclaration sur l'honneur"] && 
               documentsByType["Déclaration sur l'honneur"].length > 0 ? (
                <>
                  <div className="documents-grid">
                    {documentsByType["Déclaration sur l'honneur"].map((doc) => (
                      <div 
                        key={doc.id} 
                        className="document-card clickable"
                        onClick={() => handleDownload(doc)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="document-content">
                          <div className="document-details">
                            <div className="document-name" style={{ textAlign: 'center' }}>{doc.type}</div>
                            <div className="document-date">
                              Dernière mise à jour le : {new Date(doc.updated_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="add-document-container">
                    {canModifyDocuments && canUpdateDocType('declaration_honneur', true) ? (
                      <Button
                        onClick={() => handleFileUpload("Déclaration sur l'honneur")}
                        value="Actualiser le document"
                        variant="secondary"
                        size="small"
                      />
                    ) : !canModifyDocuments ? (
                      <div className="non-updatable-notice" style={{ 
                        fontSize: '0.85rem', 
                        color: '#666', 
                        fontStyle: 'italic',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                      }}>
                        Seul l'administrateur peut modifier les documents
                      </div>
                    ) : (
                      <div className="non-updatable-notice" style={{ 
                        fontSize: '0.85rem', 
                        color: '#666', 
                        fontStyle: 'italic',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                      }}>
                        Pour des raisons legales, veuillez faire une demande au support pour modification.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="add-document-container">
                  {canModifyDocuments && canUpdateDocType('declaration_honneur', false) ? (
                    <Button
                      onClick={() => handleFileUpload("Déclaration sur l'honneur")}
                      value="Ajouter le document"
                      variant="primary"
                      size="small"
                    />
                  ) : (
                    <div className="info-text">Aucun document disponible</div>
                  )}
                </div>
              )}
          </SectionPliable>

          {/* Sections pour Agréments et CV - toujours visibles */}
          <SectionPliable title="Agréments, certifications, habilitations">
              {documentsByType['Agréments, certifications, habilitations'] && 
               documentsByType['Agréments, certifications, habilitations'].length > 0 ? (
                <>
                  <div className="documents-grid">
                    {documentsByType['Agréments, certifications, habilitations'].map((doc) => (
                      <div 
                        key={doc.id} 
                        className="document-card clickable"
                        onClick={() => handleDownload(doc)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="document-content">
                          <div className="document-details">
                            <div className="document-name" style={{ textAlign: 'center' }}>{doc.type}</div>
                            <div className="document-date">
                              Dernière mise à jour le : {new Date(doc.updated_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="add-document-container">
                    {canModifyDocuments ? (
                      <Button
                        onClick={() => handleFileUpload('Agréments, certifications, habilitations')}
                        value="Actualiser le document"
                        variant="secondary"
                        size="small"
                      />
                    ) : (
                      <div className="non-updatable-notice" style={{ 
                        fontSize: '0.85rem', 
                        color: '#666', 
                        fontStyle: 'italic',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                      }}>
                        Seul l'administrateur peut modifier les documents
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="add-document-container">
                  {canModifyDocuments ? (
                    <Button
                      onClick={() => handleFileUpload('Agréments, certifications, habilitations')}
                      value="Ajouter le document"
                      variant="primary"
                      size="small"
                    />
                  ) : (
                    <div className="info-text">Aucun document disponible</div>
                  )}
                </div>
              )}
          </SectionPliable>

          <SectionPliable title="CV de l'equipe encadrante">
              {documentsByType["CV de l'équipe encadrante"] && 
               documentsByType["CV de l'équipe encadrante"].length > 0 ? (
                <>
                  <div className="documents-grid">
                    {documentsByType["CV de l'équipe encadrante"].map((doc) => (
                      <div 
                        key={doc.id} 
                        className="document-card clickable"
                        onClick={() => handleDownload(doc)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="document-content">
                          <div className="document-details">
                            <div className="document-name" style={{ textAlign: 'center' }}>{doc.type}</div>
                            <div className="document-date">
                              Dernière mise à jour le : {new Date(doc.updated_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="add-document-container">
                    {/* CORE-28: cv_equipe supprimé - pas de document d'équipe pour les entités */}
                    {canModifyDocuments ? (
                      <Button
                        onClick={() => handleFileUpload("CV de l'équipe encadrante")}
                        value="Actualiser le document"
                        variant="secondary"
                        size="small"
                      />
                    ) : !canModifyDocuments ? (
                      <div className="non-updatable-notice" style={{ 
                        fontSize: '0.85rem', 
                        color: '#666', 
                        fontStyle: 'italic',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                      }}>
                        Seul l'administrateur peut modifier les documents
                      </div>
                    ) : (
                      <div className="non-updatable-notice" style={{ 
                        fontSize: '0.85rem', 
                        color: '#666', 
                        fontStyle: 'italic',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                      }}>
                        Pour des raisons legales, veuillez faire une demande au support pour modification.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="add-document-container">
                  {/* CORE-28: cv_equipe supprimé - pas de document d'équipe pour les entités */}
                  {canModifyDocuments ? (
                    <Button
                      onClick={() => handleFileUpload("CV de l'équipe encadrante")}
                      value="Ajouter le document"
                      variant="primary"
                      size="small"
                    />
                  ) : (
                    <div className="info-text">Aucun document disponible</div>
                  )}
                </div>
              )}
          </SectionPliable>

          <SectionPliable title="Contrat Centralis (CCAP+AE)">
              {contrats.length > 0 ? (
                <div className="documents-grid">
                  {contrats.map((contrat) => (
                    <React.Fragment key={contrat.id}>
                      {contrat.documents.length > 0 ? (
                        contrat.documents.map((doc, index) => (
                          <div 
                            key={`${contrat.id}-${doc.type}-${index}`}
                            className="document-card clickable"
                            onClick={() => handleContratDownload(doc, contrat.numero)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="document-content">
                              <div className="document-details">
                                <div className="document-name" style={{ textAlign: 'center' }}>{doc.nom}</div>
                                <div className="document-date">
                                  Dernière mise à jour le : {new Date(contrat.updated_at).toLocaleDateString('fr-FR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="document-card" style={{ cursor: 'default' }}>
                          <div className="document-content">
                            <div className="document-details">
                              <div className="document-name" style={{ textAlign: 'center' }}>Contrat {contrat.numero}</div>
                              <div className="document-date" style={{ color: '#999' }}>
                                Documents en cours de preparation
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>Aucun contrat disponible</p>
                </div>
              )}
          </SectionPliable>
        </>
      )}
      
      {/* Modal d'upload */}
      <UploadDocumentModal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setSelectedDocType('');
        }}
        onUpload={handleUploadDocument}
        preselectedType={selectedDocType}
      />
    </div>
  );
};

export default MesDocuments;